import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Phone, EnvelopeSimple, MapPin, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import { COMPANY, SERVICES } from "@/lib/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      setSubmitted(true);
      setForm(INITIAL);
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not send your message. Please try again or call us."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-right" richColors />
      <PageHeader
        eyebrow="— Contact"
        title="Got a project? Let's talk it through."
        intro="Quick questions, ballpark numbers, or a proper site quote — happy to chat either way. We'll usually get back to you the same day."
      />

      <section className="section-pad bg-[#F6F5F2]">
        <div className="container-fz grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Contact details */}
          <div className="lg:col-span-5">
            <p className="eyebrow mb-6">— Direct lines</p>
            <h2 className="font-display text-3xl md:text-4xl text-stone-900 leading-tight">
              The quickest way? Pick up the phone.
            </h2>

            <ul className="mt-12 space-y-px border border-[#D2D0C9] bg-[#D2D0C9]">
              <li className="bg-white p-7 flex items-start gap-5">
                <Phone size={28} weight="duotone" className="text-[#D05322] shrink-0 mt-1" />
                <div>
                  <p className="eyebrow text-stone-500 mb-2">Phone</p>
                  <a
                    href={COMPANY.phoneHref}
                    data-testid="contact-phone-link"
                    className="font-display text-2xl md:text-3xl text-stone-900 hover:text-[#D05322]"
                  >
                    {COMPANY.phone}
                  </a>
                </div>
              </li>
              <li className="bg-white p-7 flex items-start gap-5">
                <EnvelopeSimple size={28} weight="duotone" className="text-[#D05322] shrink-0 mt-1" />
                <div className="min-w-0">
                  <p className="eyebrow text-stone-500 mb-2">Email</p>
                  <a
                    href={COMPANY.emailHref}
                    data-testid="contact-email-link"
                    className="text-lg md:text-xl text-stone-900 hover:text-[#D05322] break-all"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </li>
              <li className="bg-white p-7 flex items-start gap-5">
                <MapPin size={28} weight="duotone" className="text-[#D05322] shrink-0 mt-1" />
                <div>
                  <p className="eyebrow text-stone-500 mb-2">Based in</p>
                  <p className="text-lg md:text-xl text-stone-900">
                    {COMPANY.location}
                  </p>
                  <p className="text-sm text-stone-500 mt-1">
                    Serving {COMPANY.region}
                  </p>
                </div>
              </li>
            </ul>

            <div className="mt-10 p-6 border border-[#D2D0C9] bg-white">
              <p className="eyebrow text-stone-500 mb-3">Hours</p>
              <p className="text-stone-700">
                Mon – Fri: 7am – 6pm<br />
                Sat: 8am – 2pm<br />
                Sun: Closed (but the phone's usually still on)
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="border border-[#D2D0C9] bg-white p-8 md:p-12">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  data-testid="contact-form-success"
                  className="text-center py-12"
                >
                  <CheckCircle size={56} weight="duotone" className="text-[#D05322] mx-auto" />
                  <h3 className="mt-6 font-display text-3xl text-stone-900">
                    Message received.
                  </h3>
                  <p className="mt-3 text-stone-600 max-w-md mx-auto">
                    Thanks for reaching out. We'll be in touch shortly — usually
                    the same day. For anything urgent, give us a call.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-form-reset"
                    className="mt-8 btn-ghost"
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                  <p className="eyebrow">— Send a message</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Name *">
                      <input
                        required
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        data-testid="contact-input-name"
                        className={inputCls}
                        placeholder="Your full name"
                      />
                    </Field>
                    <Field label="Email *">
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        data-testid="contact-input-email"
                        className={inputCls}
                        placeholder="you@example.com"
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Phone">
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="contact-input-phone"
                        className={inputCls}
                        placeholder="021 123 4567"
                      />
                    </Field>
                    <Field label="Service">
                      <select
                        value={form.service}
                        onChange={update("service")}
                        data-testid="contact-input-service"
                        className={`${inputCls} appearance-none bg-white`}
                      >
                        <option value="">Select a service…</option>
                        {SERVICES.map((s) => (
                          <option key={s.slug} value={s.title}>{s.title}</option>
                        ))}
                        <option value="Other">Other / Not sure yet</option>
                      </select>
                    </Field>
                  </div>
                  <Field label="Tell us about the job *">
                    <textarea
                      required
                      rows={6}
                      value={form.message}
                      onChange={update("message")}
                      data-testid="contact-input-message"
                      className={`${inputCls} resize-y`}
                      placeholder="Location, rough size, timeframe, anything else we should know…"
                    />
                  </Field>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="btn-primary w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending…" : "Send message"}
                    {!submitting && <PaperPlaneTilt size={16} weight="bold" />}
                  </button>
                  <p className="text-xs text-stone-500 leading-relaxed">
                    By submitting, you agree we may contact you about your enquiry.
                    We don't share your details with anyone else.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-[#F6F5F2] border border-[#D2D0C9] focus:border-[#D05322] focus:outline-none focus:ring-0 px-4 py-3 text-base text-stone-900 placeholder:text-stone-400 transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-700 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
